import React from 'react'
import ContentModule from '../ContentModule'

import useContentfulColors from '../../hooks/useContentfulColors'
import { ColorWithAccent } from '../../hooks/useContentfulColors/useContentfulColors'
import { ConditionalWrapper } from '../utils'
import { mapFlexAlignTextAndItems } from '../..//utils/getFlexBoxAlignType'
import { IContentModule } from '../ContentModule/types'
import { BLOCKS, RenderNode } from '../RichText/types'

// MUI
import Grid, { GridSize } from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import { useTheme } from '@mui/material/styles'
import { IBoxModule } from './types'

const BoxModule = ({ title, contentAlignment = 'start', subtitle, boxes, hasBorders }: IBoxModule) => {
  const { breakpoints, palette } = useTheme()

  const styles = {
    title: {
      paddingBottom: '32px',
      [breakpoints.up('md')]: {
        paddingBottom: '44px',
      },
    },
    subtitle: {
      maxWidth: '100%',
      paddingBottom: '32px',
      [breakpoints.up('md')]: {
        maxWidth: '50%',
        paddingBottom: '44px',
      },
    },
    boxModuleImage: {
      borderRadius: '6px',
      maxHeight: '250px',
      marginBottom: '16px',
    },
    richtextStyles: {
      '& > *:last-child': {
        marginBottom: '0px !important',
      },
      '& > div:has(.MuiButton-root):last-child': {
        marginTop: 'auto',
      },
    },
    h3: {
      wordBreak: 'break-word',
      marginBottom: '.7em',
    },
    h2: {
      scrollMarginTop: '80px',
      marginBottom: '.5em',
      wordBreak: 'break-word',
      '&:only-child': {
        marginTop: '-5px',
      },
      '&:first-child': {
        marginTop: 0,
      },
    },
  }

  const renderNode: Partial<RenderNode> = {
    [BLOCKS.HEADING_2]: (node, children) => {
      const element = (
        <Typography component="h2" variant={'h5'} sx={styles.h2}>
          {children}
        </Typography>
      )
      return element
    },

    [BLOCKS.HEADING_3]: (node, children) => {
      const element = (
        <Typography component="h3" variant={'h5'} sx={styles.h3}>
          {children}
        </Typography>
      )
      return element
    },
  }
  const { length: numberOfBoxes } = (boxes && boxes) || { length: 0 }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      textAlign={mapFlexAlignTextAndItems(contentAlignment)}
      alignItems={mapFlexAlignTextAndItems(contentAlignment)}
    >
      {title && (
        <Typography color="primary" component="h2" variant="h5" sx={styles.title}>
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography component={title ? 'p' : 'h2'} variant="h3" sx={styles.subtitle}>
          {subtitle}
        </Typography>
      )}
      <Grid container spacing={3} justifyContent="center">
        {boxes ? (
          boxes?.map((content, index) => {
            const contentColorMap = useContentfulColors()
            const { bgColor }: IContentModule = content!
            const boxColorOptions = contentColorMap.get(bgColor ?? palette.common.black) as ColorWithAccent

            const fitInMultipleRows = (12 / Math.ceil(numberOfBoxes / 2)) as GridSize
            const fitInOneRow = Math.ceil(12 / numberOfBoxes) as GridSize

            const gridSize = numberOfBoxes > 4 ? fitInMultipleRows : fitInOneRow

            return (
              <Grid xs={12} sm={6} md={gridSize} key={index} item>
                <ConditionalWrapper
                  wrapper={children => (
                    <Card
                      elevation={0}
                      style={{
                        color: boxColorOptions[1],
                        backgroundColor: boxColorOptions[0],
                        height: '100%',
                        outline: `solid ${hasBorders ? 1 : 0}px ${boxColorOptions[1]}`,
                      }}
                    >
                      <CardContent style={{ height: '100%' }}>{children}</CardContent>
                    </Card>
                  )}
                  condition={!!bgColor}
                >
                  <ContentModule
                    {...content}
                    sx={{
                      richText: styles.richtextStyles,
                      image: styles.boxModuleImage,
                    }}
                    renderNode={renderNode}
                  />
                </ConditionalWrapper>
              </Grid>
            )
          })
        ) : (
          <></>
        )}
      </Grid>
    </Box>
  )
}

export default BoxModule
